import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import useReveal from "@/hooks/useReveal";
import { setPageSEO } from "@/lib/seo";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppFloat from "@/components/landing/WhatsAppFloat";
import LeadMagnetForm from "@/components/landing/LeadMagnetForm";
import { CheckCircle2, Mail, BookOpen, Users, TrendingUp } from "lucide-react";

const BOOK_COVER =
  "https://customer-assets-eiarnc6j.emergentagent.net/job_33f49ecb-da23-4d9b-8b06-c4f8fb2c8137/artifacts/3e9654809918e9d8_9781946885852.RH.0.x.webp";

const BENEFITS = [
  {
    icon: BookOpen,
    title: "El origen del sistema EOS",
    text: "El capítulo que introduce por qué más de 34.000 empresas en el mundo usan esta metodología para ordenar su negocio.",
  },
  {
    icon: TrendingUp,
    title: "Los 6 componentes clave",
    text: "La primera mirada a Visión, Personas, Datos, Asuntos, Procesos y Tracción — el mapa completo de EOS.",
  },
  {
    icon: Users,
    title: "Escrito para dueños de negocio",
    text: "Gino Wickman explica en lenguaje simple por qué tu empresa no necesita más esfuerzo, sino más sistema.",
  },
];

export default function LibroTraccion() {
  useReveal();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");

  const utm = {
    utm_source: searchParams.get("utm_source") || "",
    utm_medium: searchParams.get("utm_medium") || "",
    utm_campaign: searchParams.get("utm_campaign") || "",
    gclid: searchParams.get("gclid") || "",
  };

  useEffect(() => {
    setPageSEO({
      title: "Descarga gratis el Capítulo 1 de Tracción (Traction) de Gino Wickman | Carlos Garcín",
      description: "Descarga gratis el Capítulo 1 del libro Tracción (Traction) de Gino Wickman, el sistema EOS para dueños de negocio. Recíbelo al instante en tu correo.",
      path: "/libro-traccion-gino-wickman",
      keywords: "Tracción Gino Wickman, Traction libro pdf, descargar capítulo 1 tracción, libro EOS gratis, resumen tracción pdf, Traction Get a Grip on Your Business",
      image: BOOK_COVER,
      imageAlt: "Tracción (Traction) de Gino Wickman — descarga gratis el Capítulo 1",
    });
    window.scrollTo(0, 0);
  }, []);

  const handleSuccess = ({ name, email }) => {
    setLeadName(name);
    setLeadEmail(email);
    setSubmitted(true);
  };

  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: "Traction: Get a Grip on Your Business",
    alternateName: "Tracción",
    author: { "@type": "Person", name: "Gino Wickman" },
    about: "Entrepreneurial Operating System (EOS)",
    url: "https://garcin.cl/libro-traccion-gino-wickman",
  };

  return (
    <>
      <Header />
      <main data-testid="libro-traccion-page">
        {/* Hero + Form */}
        <section className="bg-[#0A1128] pt-28 pb-20 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,1) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />
          <div className="absolute -top-20 -right-20 w-[480px] h-[480px] rounded-full bg-[#F26522] opacity-10 blur-3xl pointer-events-none" />

          <div className="container-eos relative">
            <div className="grid lg:grid-cols-[1.05fr_420px] gap-12 lg:gap-16 items-center">
              <div>
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase text-[#F26522]">
                  Recurso gratuito · Traction Library
                </p>
                <h1 className="mt-4 font-display font-extrabold text-white leading-[1.05] tracking-[-0.02em] text-3xl sm:text-4xl lg:text-5xl max-w-2xl">
                  Descarga gratis el <span className="text-[#F26522]">Capítulo 1</span> de Tracción, de Gino Wickman
                </h1>
                <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-xl leading-relaxed">
                  El libro fundacional del sistema EOS, con más de 2 millones de copias vendidas. Recibe el primer capítulo gratis en tu correo, sin costo.
                </p>

                {!submitted ? (
                  <div className="mt-8 max-w-xl rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-6 sm:p-7">
                    <LeadMagnetForm utm={utm} onSuccess={handleSuccess} />
                  </div>
                ) : (
                  <div
                    data-testid="lead-magnet-success"
                    className="mt-8 max-w-xl rounded-2xl border border-[#F26522]/30 bg-white/5 backdrop-blur-sm p-7 sm:p-8"
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-8 h-8 text-[#F26522] shrink-0" />
                      <h2 className="font-display font-extrabold text-xl text-white">
                        ¡Listo, {leadName.split(" ")[0]}!
                      </h2>
                    </div>
                    <p className="mt-3 text-slate-300 leading-relaxed flex items-start gap-2">
                      <Mail className="w-4 h-4 text-[#F26522] shrink-0 mt-1" />
                      <span>
                        Te enviamos el Capítulo 1 a <strong className="text-white">{leadEmail}</strong>. Revisa tu bandeja de entrada (y la carpeta de spam) en los próximos minutos.
                      </span>
                    </p>
                  </div>
                )}
              </div>

              <div className="reveal flex justify-center lg:justify-end">
                <div className="relative w-full max-w-sm">
                  <img
                    src={BOOK_COVER}
                    alt="Tracción (Traction) de Gino Wickman — descarga gratis el Capítulo 1"
                    className="w-full rounded-2xl shadow-2xl shadow-black/40"
                    loading="eager"
                  />
                  <div
                    data-testid="lead-magnet-badge"
                    className="absolute -bottom-4 -right-4 w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#F26522] flex items-center justify-center text-center shadow-lg shadow-black/30 border-4 border-[#0A1128] rotate-[-8deg]"
                  >
                    <span className="text-white font-display font-extrabold text-[11px] sm:text-xs leading-tight uppercase tracking-wide">
                      Capítulo 1<br />Gratis
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section data-testid="libro-traccion-benefits" className="section-padding bg-white">
          <div className="container-eos">
            <div className="max-w-2xl reveal">
              <span className="eyebrow">Qué encontrarás en el Capítulo 1</span>
              <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-navy-800 leading-tight">
                Un primer vistazo al sistema que{" "}
                <span className="text-[#F26522]">ordena tu empresa</span>
              </h2>
            </div>
            <div className="mt-12 grid sm:grid-cols-3 gap-6">
              {BENEFITS.map((b, i) => (
                <div
                  key={b.title}
                  className="reveal rounded-2xl border border-slate-200 p-6 hover:border-[#F26522]/40 hover:shadow-lg transition-all"
                  style={{ transitionDelay: `${i * 70}ms` }}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#FEF3EB] flex items-center justify-center">
                    <b.icon className="w-5 h-5 text-[#F26522]" strokeWidth={1.8} />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-lg text-navy-800">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{b.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA to full implementation */}
        <section data-testid="libro-traccion-cta" className="py-16 bg-slate-50/60">
          <div className="container-eos">
            <div className="reveal rounded-2xl border border-slate-200 bg-white p-8 lg:p-10 flex flex-col sm:flex-row items-center justify-between gap-6">
              <div>
                <p className="font-display font-extrabold text-2xl text-navy-800 leading-tight">
                  ¿Ya leíste Tracción y quieres implementarlo en tu empresa?
                </p>
                <p className="mt-2 text-slate-600">
                  Carlos Garcín es Professional EOS Implementer certificado en Chile y Latinoamérica.
                </p>
              </div>
              <a href="/contacto" data-testid="libro-traccion-contacto-link" className="btn-primary shrink-0">
                Agendar una llamada
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
      <Toaster position="top-right" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(bookSchema) }}
      />
    </>
  );
}
